import React from 'react';
import { ChevronDown } from 'lucide-react';
import MemberPhoto from './MemberPhoto';

/**
 * Club_CartCard (Figma 3273:28789 plegada · 3275:33930 desplegada).
 *
 * Una tarjeta por SOCIO con lo que debe, en la rejilla de /carritos. Plegada
 * son 64 de alto: foto, nombre, «Socio #N · Estado» y el total. Al desplegar
 * aparecen las acciones de cobro.
 *
 * Agrupa por socio y no por carrito a propósito: un mismo socio puede tener
 * pendiente uno del dispensario y otro de la barra, y quien cobra piensa en
 * «lo que debe Pablo», no en dos apuntes sueltos. Por eso el total es la suma
 * y hay un «Pagar todo».
 *
 * La cabecera entera es el disparador, no solo la flecha: en una tablet
 * acertar a un chevron de 16 px con el dedo es pedir demasiado.
 */
export default function CartCard({
  /** { id, name, number, status, photo } */
  member,
  total = 0,
  count = 1,
  expanded = false,
  onToggle,
  children = null,
  className = '',
}) {
  const idContenido = `cart-card-${member?.id}`;

  /*
   * Status=Selected del frame: el borde pasa a --primary y aparece un anillo de
   * 2 px. En Figma la variable se llama `ring` y resuelve a #475569 SÓLIDO; ojo,
   * el `--ring` del código NO es eso —es ese mismo slate al 15%—, así que el
   * borde va por --primary y el anillo por --ring-selected, que sí coincide con
   * el rgba(100,116,139,.35) de la sombra del frame.
   *
   * El radio del frame es 6 = `rounded-sm` en esta escala. `rounded-lg` aquí
   * vale 10 y redondeaba de más.
   */
  const marco = expanded
    ? 'border-[var(--primary)] shadow-[0_0_0_2px_var(--ring-selected)]'
    : 'border-[var(--border)]';

  return (
    <div
      data-testid="cart-card"
      data-expanded={expanded ? 'true' : 'false'}
      className={`flex flex-col overflow-hidden rounded-sm border bg-[var(--card)] transition-shadow ${marco} ${className}`}
    >
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={expanded}
        aria-controls={idContenido}
        className="flex h-16 w-full items-center gap-3 px-3 text-left transition-colors hover:bg-[var(--muted)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[var(--primary)]"
      >
        <MemberPhoto
          path={member?.photo}
          name={member?.name}
          className="h-10 w-10 flex-shrink-0 rounded-md object-cover"
        />

        <div className="min-w-0 flex-1">
          <div className="truncate text-sm font-bold text-[var(--foreground)]">
            {member?.name}
          </div>
          <div className="truncate text-xs text-[var(--muted-foreground)]">
            Socio #{member?.number ?? '—'}
            {member?.status ? ` · ${member.status}` : ''}
            {/* Con dos carritos el total suma dos áreas; decirlo evita que el
                importe parezca un error de cuenta. */}
            {count > 1 ? ` · ${count} carritos` : ''}
          </div>
        </div>

        <span className="flex-shrink-0 text-sm font-bold tabular-nums text-[var(--foreground)]">
          €{Number(total).toFixed(2)}
        </span>
        <ChevronDown
          size={16}
          strokeWidth={1.5}
          aria-hidden
          className={`flex-shrink-0 text-[var(--muted-foreground)] transition-transform ${
            expanded ? 'rotate-180' : ''
          }`}
        />
      </button>

      {expanded && (
        <div id={idContenido} className="border-t border-[var(--border)] p-3">
          {children}
        </div>
      )}
    </div>
  );
}
